import { useEffect, useRef, useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { EmptyState } from '@/components/EmptyState';
import { LoadErrorState, NoMatchesState } from '@/components/ListStates';
import { aiStudioService, AiGeneration, AiGenerationKind } from '@/services/ai-studio.service';
import {
  Images, Search, Upload, Loader2, Film, FileText, ImageIcon,
  Download, Trash2, Sparkles, ExternalLink, Play,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

type Filter = 'all' | 'image' | 'video' | 'copy' | 'combined';
const PAGE_SIZE = 24;

const FILTERS: { v: Filter; label: string }[] = [
  { v: 'all', label: 'All' },
  { v: 'image', label: 'Images' },
  { v: 'video', label: 'Videos' },
  { v: 'combined', label: 'Combined' },
  { v: 'copy', label: 'Copy' },
];

/** Cross-origin (ImageKit) download via blob so the filename + save dialog work. */
async function downloadAsset(url: string, filename: string) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  } catch {
    window.open(url, '_blank');
  }
}

/**
 * ImageKit renders a still frame from any hosted video by appending
 * `/ik-thumbnail.jpg`. `tr=so-1` grabs a frame ~1s in, skipping a black
 * opening frame. Returns null for non-ImageKit URLs.
 */
function ikVideoThumb(url?: string): string | null {
  if (!url || !url.includes('ik.imagekit.io')) return null;
  return `${url}/ik-thumbnail.jpg?tr=so-1`;
}

/** Real video thumbnail with a graceful film-icon fallback + skeleton while loading. */
function VideoThumb({ url }: { url?: string }) {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const thumb = ikVideoThumb(url);
  if (!thumb || failed) {
    return <div className="flex h-full w-full items-center justify-center bg-black/80"><Film size={26} className="text-white/80" /></div>;
  }
  return (
    <div className="relative h-full w-full bg-black/80">
      {!loaded && <div className="absolute inset-0 flex items-center justify-center"><Film size={26} className="animate-pulse text-white/40" /></div>}
      <img
        src={thumb}
        alt=""
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => setFailed(true)}
        className={clsx('h-full w-full object-cover transition-opacity', loaded ? 'opacity-100' : 'opacity-0')}
      />
      <span className="absolute bottom-1.5 right-1.5 rounded-full bg-black/60 p-1 text-white"><Play size={11} /></span>
    </div>
  );
}

export default function MediaLibrary() {
  const [items, setItems] = useState<AiGeneration[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  // The search box only applies on Enter / Search, so the empty state follows what was last sent.
  const [appliedQ, setAppliedQ] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState<AiGeneration | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async (opts: { page: number; filter: Filter; q: string }) => {
    setLoading(true);
    setLoadError(false);
    setAppliedQ(opts.q);
    try {
      // "Combined" is a source filter (kind is still video); the rest are kinds.
      const kind: AiGenerationKind | undefined =
        opts.filter === 'all' || opts.filter === 'combined' ? undefined : opts.filter;
      const source = opts.filter === 'combined' ? 'combined' as const : undefined;
      const r = await aiStudioService.list({ kind, source, q: opts.q || undefined, page: opts.page, limit: PAGE_SIZE });
      setItems(r.items);
      setTotal(r.total);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const filtersActive = filter !== 'all' || !!appliedQ.trim();
  const clearFilters = () => {
    setQ('');
    // Changing the filter reloads through its effect, which then sees the cleared search.
    if (filter !== 'all') setFilter('all');
    else { setPage(1); void load({ page: 1, filter: 'all', q: '' }); }
  };

  useEffect(() => {
    setPage(1);
    load({ page: 1, filter, q });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const mime = file.type || '';
    if (!mime.startsWith('image/') && !mime.startsWith('video/')) {
      toast.error('Only images and videos can be uploaded');
      return;
    }
    setUploading(true);
    try {
      await aiStudioService.uploadMedia(file);
      toast.success('Uploaded to library');
      setPage(1);
      await load({ page: 1, filter, q });
    } catch {
      /* interceptor surfaces */
    } finally {
      setUploading(false);
    }
  };

  const remove = async (g: AiGeneration) => {
    if (!window.confirm('Permanently delete this asset? This removes the file from ImageKit and cannot be undone.')) return;
    try {
      const r = await aiStudioService.remove(g.generationUid);
      setItems((prev) => prev.filter((x) => x.generationUid !== g.generationUid));
      setViewing(null);
      if (r.imagekitDeleted === false) {
        toast('Removed from library — but the file may still be on ImageKit (delete failed). Check ImageKit if storage matters.', { icon: '⚠️' });
      } else {
        toast.success('Deleted from library and ImageKit');
      }
    } catch {
      /* interceptor surfaces */
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <Topbar
        title="Media Library"
        breadcrumb="Library"
        description="Every image, video, and caption you've generated or uploaded — reusable across WhatsApp, Facebook, Instagram, TikTok, Google and YouTube."
        right={
          <>
            <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={onUpload} />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} iconLeft={uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}>
              Upload
            </Button>
          </>
        }
      />

      <div className="w-full px-7 py-7 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1.5">
            {FILTERS.map((f) => (
              <button
                key={f.v}
                onClick={() => setFilter(f.v)}
                className={clsx(
                  'rounded-full px-3 py-1.5 text-xs font-semibold transition',
                  filter === f.v ? 'bg-primary text-on-primary' : 'bg-surface-alt text-text-muted hover:text-text-primary',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Input
              placeholder="Search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load({ page: 1, filter, q }); } }}
              iconLeft={<Search size={14} />}
            />
            <Button variant="secondary" onClick={() => { setPage(1); load({ page: 1, filter, q }); }}>Search</Button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {[...Array(10)].map((_, i) => <div key={i} className="skeleton aspect-square rounded-xl" />)}
          </div>
        ) : loadError ? (
          <LoadErrorState what="your media library" onRetry={() => void load({ page, filter, q: appliedQ })} />
        ) : items.length === 0 && filtersActive ? (
          <NoMatchesState onClear={clearFilters} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Images size={40} className="text-text-muted" />}
            title="Your library is empty"
            description="Generate images, videos, or copy in AI Studio, or upload files here. They'll be reusable everywhere you build a template or campaign."
            action={<Button iconLeft={<Sparkles size={14} />} onClick={() => { window.location.href = '/ai-studio'; }}>Open AI Studio</Button>}
          />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {items.map((g) => (
                <button
                  key={g.generationUid}
                  type="button"
                  onClick={() => setViewing(g)}
                  className="group relative overflow-hidden rounded-xl border border-border bg-surface-alt text-left transition hover:border-primary/50 hover:shadow-soft"
                >
                  <div className="aspect-square w-full">
                    {g.kind === 'image' && (g.thumbnailUrl || g.assetUrl) ? (
                      <img src={g.thumbnailUrl ?? g.assetUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
                    ) : g.kind === 'video' ? (
                      <VideoThumb url={g.assetUrl} />
                    ) : (
                      <div className="flex h-full w-full flex-col gap-1 overflow-hidden bg-surface p-3">
                        <FileText size={16} className="text-primary" />
                        <p className="line-clamp-5 text-[11px] leading-snug text-text-secondary">{g.copyText ?? g.prompt}</p>
                      </div>
                    )}
                  </div>
                  <span className="absolute left-1.5 top-1.5 inline-flex items-center gap-0.5 rounded bg-black/55 px-1.5 py-0.5 text-[9px] font-medium uppercase text-white">
                    {g.kind === 'image' ? <ImageIcon size={9} /> : g.kind === 'video' ? <Film size={9} /> : <FileText size={9} />}
                    {g.source === 'combined' ? 'combined' : g.source === 'uploaded' ? 'upload' : g.kind === 'copy' ? 'copy' : 'ai'}
                  </span>
                </button>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 pt-2 text-xs text-text-muted">
                <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => { const p = page - 1; setPage(p); load({ page: p, filter, q }); }}>Prev</Button>
                <span>Page {page} / {totalPages} · {total} items</span>
                <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => { const p = page + 1; setPage(p); load({ page: p, filter, q }); }}>Next</Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Lightbox / detail viewer */}
      <Modal open={!!viewing} onClose={() => setViewing(null)} title={viewing?.title ?? viewing?.prompt?.slice(0, 60) ?? 'Asset'} widthClass="max-w-2xl">
        {viewing && (
          <div className="space-y-3">
            <div className="overflow-hidden rounded-xl border border-border bg-surface-alt">
              {viewing.kind === 'image' && viewing.assetUrl ? (
                <img src={viewing.assetUrl} alt="" className="max-h-[60vh] w-full object-contain" />
              ) : viewing.kind === 'video' && viewing.assetUrl ? (
                <video src={viewing.assetUrl} controls className="max-h-[60vh] w-full" />
              ) : viewing.kind === 'audio' && viewing.assetUrl ? (
                <audio src={viewing.assetUrl} controls className="w-full p-4" />
              ) : (
                <pre className="max-h-[50vh] overflow-y-auto whitespace-pre-wrap p-4 text-sm text-text-primary">{viewing.copyText ?? viewing.prompt}</pre>
              )}
            </div>
            <div className="text-xs text-text-muted">
              <span className="capitalize">{viewing.kind}</span>
              {viewing.source === 'uploaded' ? ' · uploaded' : ` · generated by ${viewing.provider}`}
              {viewing.durationSec ? ` · ~${viewing.durationSec}s` : ''}
              {' · '}{new Date(viewing.createdAt).toLocaleString()}
            </div>
            {viewing.prompt && viewing.source !== 'uploaded' && (
              <p className="rounded-lg bg-surface-alt/50 px-3 py-2 text-xs text-text-secondary">{viewing.prompt}</p>
            )}
            <div className="flex justify-end gap-2">
              {viewing.assetUrl && (
                <>
                  <a href={viewing.assetUrl} target="_blank" rel="noreferrer">
                    <Button variant="ghost" iconLeft={<ExternalLink size={13} />}>Open</Button>
                  </a>
                  <Button
                    variant="secondary"
                    iconLeft={<Download size={13} />}
                    onClick={() => downloadAsset(viewing.assetUrl!, viewing.originalFilename ?? `${viewing.kind}-${viewing.generationUid}`)}
                  >
                    Download
                  </Button>
                </>
              )}
              <Button variant="ghost" className="text-danger" iconLeft={<Trash2 size={13} />} onClick={() => remove(viewing)}>Delete</Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
